import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
  Alert
} from '@mui/material';
import { toast } from 'react-toastify';
import LoadingSpinner from './LoadingSpinner';
import LanguageDropdown from './LanguageDropdown';
import i18n from '../i18n';
import './quickOnboarding/QuickOnboarding.scss';

// Step components
import AccountCreationStep from './quickOnboarding/AccountCreationStep';
import QuickShowcaseStep from './quickOnboarding/QuickShowcaseStep';
import QuickProfileStep from './quickOnboarding/QuickProfileStep';
import ProfileSetupComplete from './quickOnboarding/ProfileSetupComplete';

interface FormData {
  // Account Creation
  email: string;
  password: string;

  // Showcase
  showcases: unknown[];
  instagramConnected: boolean;

  // Profile
  displayName: string;
  description: string;
  businessAddress: string;
  coordinates: number[];
  placeId: string;
  region: string;
  businessArea: string;
  primaryServiceType: string;
  phoneNumber: string;

  // Internal
  stylistId: string | null;
  userId: string | null;
  businessId: string | null;

  // AI Analysis results
  aiAnalysis: unknown;

  // Dynamic fields from processInstagramData
  processedStaffId?: string;
  processedBusinessId?: string;

  [key: string]: unknown;
}

interface NavigationState {
  initialStep?: number;
  formData?: Partial<FormData>;
  [key: string]: unknown;
}

interface StepDefinition {
  key: string;
  label: string;
  description: string;
}

interface OnboardingProgress {
  activeStep: number;
  formData: Partial<FormData>;
  language: string;
  timestamp: number;
}

const QuickOnboarding: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get initial data from navigation state if present
  const navigationState = (location.state || {}) as NavigationState;

  // Stepper state
  const [activeStep, setActiveStep] = useState<number>(navigationState.initialStep || 0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Language state - load from localStorage first, fallback to i18n.language
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    return savedLanguage || i18n.language;
  });

  // Flag to prevent saving during initial load
  const isLoadingProgress = useRef<boolean>(true);

  // Utility function to scroll to top
  const scrollToTop = (): void => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // AI Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [aiAnalysisError, setAiAnalysisError] = useState<string | null>(null);

  // Form data state
  const [formData, setFormData] = useState<FormData>({
    // Account Creation
    email: '',
    password: '',

    // Showcase
    showcases: [],
    instagramConnected: false,

    // Profile
    displayName: '',
    description: '',
    businessAddress: '',
    coordinates: [],
    placeId: '',

    // Merge any data from navigation state
    ...navigationState.formData,
    region: '',
    businessArea: '',
    primaryServiceType: '',
    phoneNumber: '',

    // Internal
    stylistId: null,
    userId: null,
    businessId: null,

    // AI Analysis results
    aiAnalysis: null
  });



  const steps: StepDefinition[] = [
    {
      key: 'account',
      label: t('quickOnboarding.stepNames.account', 'Create Account'),
      description: t('quickOnboarding.stepNames.account', 'Create Account')
    },
    {
      key: 'showcase',
      label: t('quickOnboarding.stepNames.showcase', 'Add Showcases'),
      description: t('quickOnboarding.stepNames.showcase', 'Add Showcases')
    },
    {
      key: 'profile',
      label: t('quickOnboarding.stepNames.profile', 'Complete Profile'),
      description: t('quickOnboarding.stepNames.profile', 'Complete Profile')
    },
    {
      key: 'complete',
      label: t('quickOnboarding.stepNames.complete', 'Setup Complete'),
      description: t('quickOnboarding.stepNames.complete', 'Setup Complete')
    }
  ];

  // Handle step navigation
  const handleNext = (): void => {
    if (activeStep < steps.length - 1) {
      setActiveStep(prev => prev + 1);
      scrollToTop();
    }
  };

  const handleBack = (): void => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
      scrollToTop();
    }
  };

  const updateFormData = (newData: Partial<FormData>): void => {
    console.log('updateFormData called with:', newData); // DEBUG
    setFormData(prev => ({ ...prev, ...newData }));
    // TEMPORARILY DISABLED ALL SAVING TO FIND LOOP SOURCE
    setTimeout(() => saveProgressToLocalStorage(), 100);
  };

  // AI Analysis functions
  const triggerInstagramAnalysis = async (stylistId: string): Promise<void> => {
    if (!stylistId || isAnalyzing) return;

    console.log('🤖 Triggering Instagram AI analysis for stylist:', stylistId);
    setIsAnalyzing(true);
    setAiAnalysisError(null);

    try {
      const { analyzeInstagramProfile } = await import('../services/instagramAnalyzeService');

      // Run AI analysis and save directly to database using processInstagramData
      const result = await analyzeInstagramProfile(stylistId, { saveToDatabase: true });
      console.log('✅ AI analysis completed:', result);

      if (result.data.savedToDatabase) {
        console.log('✅ AI analysis data saved to database via processInstagramData');
        console.log(`   - Staff ID: ${result.data.staffId}`);
        console.log(`   - Business ID: ${result.data.businessId}`);

        // Store the staffId for the profile step
        setFormData(prev => ({
          ...prev,
          processedStaffId: result.data.staffId as string,
          processedBusinessId: result.data.businessId as string
        }));

        toast.success(t('quickOnboarding.aiAnalysisComplete', 'Instagram profile analysis completed and saved to database!'));
      } else {
        console.log('ℹ️ AI analysis completed but not saved to database');
        toast.warning(t('quickOnboarding.aiAnalysisPartialSuccess', 'Instagram analysis completed! You can fill out your profile manually on the next step.'));
      }
    } catch (error) {
      console.error('❌ AI analysis failed:', error);
      const analysisError = error as { message: string; code?: string };
      setAiAnalysisError(analysisError.message);

      // Show user-friendly error messages
      if (analysisError.code === 'INSTAGRAM_NOT_CONNECTED') {
        toast.warning(t('quickOnboarding.aiAnalysisErrors.notConnected', 'Please connect Instagram first to enable profile analysis.'));
      } else if (analysisError.code === 'INSUFFICIENT_DATA') {
        toast.warning(t('quickOnboarding.aiAnalysisErrors.insufficientData', 'Not enough Instagram data for analysis. Try adding more posts with captions.'));
      } else if (analysisError.code === 'AI_NOT_CONFIGURED') {
        toast.error(t('quickOnboarding.aiAnalysisErrors.notConfigured', 'AI service temporarily unavailable.'));
      } else if (analysisError.code === 'AI_RATE_LIMIT') {
        toast.warning(t('quickOnboarding.aiAnalysisErrors.rateLimit', 'Analysis service busy. Please try again in a few minutes.'));
      } else {
        toast.error(t('quickOnboarding.aiAnalysisErrors.general', 'Profile analysis failed. You can still fill out your profile manually.'));
      }
    } finally {
      setIsAnalyzing(false);
      // Reset analysis trigger to allow future manual triggers
      if (window.isAnalysisTriggeredRef) {
        window.isAnalysisTriggeredRef.current = false;
      }
    }
  };

  const saveProgressToLocalStorage = (): void => {
    // Don't save during initial loading
    if (isLoadingProgress.current) return;

    try {
      const progress: OnboardingProgress = {
        activeStep: activeStep + 1, // Save next step
        formData: formData,
        language: localStorage.getItem('preferredLanguage') || i18n.language,
        timestamp: Date.now()
      };
      localStorage.setItem('quickOnboardingProgress', JSON.stringify(progress));
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const loadProgressFromLocalStorage = useCallback((): boolean => {
    console.log('loadProgressFromLocalStorage called'); // DEBUG
    try {
      const saved = localStorage.getItem('quickOnboardingProgress');
      if (saved) {
        const progress: OnboardingProgress = JSON.parse(saved);
        // Only load if saved within last 24 hours
        if (Date.now() - progress.timestamp < 24 * 60 * 60 * 1000) {
          console.log('Restoring progress:', progress); // DEBUG
          // Subtract 1 because save function adds +1 to activeStep
          const restoredStep = Math.max(0, (progress.activeStep || 1) - 1);
          // Ensure step doesn't exceed available steps (0, 1, 2, 3)
          const clampedStep = Math.min(restoredStep, 3);
          setActiveStep(clampedStep);
          setFormData(prev => ({ ...prev, ...progress.formData }));

          // Restore language preference if it exists and is different
          if (progress.language && progress.language !== i18n.language) {
            console.log('Language needs to be changed from', i18n.language, 'to', progress.language); // DEBUG
            setCurrentLanguage(progress.language);
            i18n.changeLanguage(progress.language);
            localStorage.setItem('preferredLanguage', progress.language);
          } else {
            console.log('Language already matches:', progress.language, '=', i18n.language, '- no change needed'); // DEBUG
          }

          return true;
        }
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
    return false;
  }, []);

  const handleLanguageChange = async (lng: string): Promise<void> => {
    try {
      setCurrentLanguage(lng);
      i18n.changeLanguage(lng);

      // Save to localStorage
      localStorage.setItem('preferredLanguage', lng);

      // If user is authenticated, update in backend
      // This will be handled later when we have user context
      toast.success(t('quickOnboarding.languageChanged'));
    } catch (error) {
      console.error('Error changing language:', error);
      toast.error(t('quickOnboarding.errors.languageChangeFailed'));
    }
  };

  // Handle navigation state changes (for direct navigation to specific steps)
  useEffect(() => {
    const navigationState = (location.state || {}) as NavigationState;
    if (navigationState.initialStep !== undefined) {
      console.log('Setting activeStep from navigation:', navigationState.initialStep);
      setActiveStep(navigationState.initialStep);
      scrollToTop();
    }
    if (navigationState.formData) {
      console.log('Setting formData from navigation:', navigationState.formData);
      setFormData(prevData => ({
        ...prevData,
        ...navigationState.formData
      }));
    }
  }, [location.state]);

  // Load progress on component mount
  useEffect(() => {
    const hasProgress = loadProgressFromLocalStorage();
    if (hasProgress) {
      toast.info(t('quickOnboarding.progressRestored'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadProgressFromLocalStorage]); // Removed t to prevent language change loops

  // Initialize language from localStorage on component mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  // Enable saving after initial component setup is complete
  useEffect(() => {
    const timer = setTimeout(() => {
      isLoadingProgress.current = false;
    }, 200); // Wait for all initial effects to complete

    return () => clearTimeout(timer);
  }, []);

  const renderStep = (): React.ReactNode => {
    switch (activeStep) {
      case 0:
        return (
          <AccountCreationStep
            formData={formData}
            onUpdate={updateFormData}
            onNext={handleNext}
            onError={setError}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        );
      case 1:
        return (
          <QuickShowcaseStep
            formData={formData as React.ComponentProps<typeof QuickShowcaseStep>['formData']}
            onUpdate={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            onError={setError}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            triggerInstagramAnalysis={triggerInstagramAnalysis}
            isAnalyzing={isAnalyzing}
            aiAnalysisError={aiAnalysisError ?? undefined}
          />
        );
      case 2:
        return (
          <QuickProfileStep
            formData={formData as React.ComponentProps<typeof QuickProfileStep>['formData']}
            onUpdate={updateFormData}
            onComplete={handleNext}
            onBack={handleBack}
            onError={setError}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            isAnalyzing={isAnalyzing}
          />
        );
      case 3:
        return (
          <ProfileSetupComplete
            formData={formData as React.ComponentProps<typeof ProfileSetupComplete>['formData']}
            disableAutoRedirect={true}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading && activeStep === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      className="quick-onboarding"
      sx={{
        minHeight: '100vh',
        py: 4
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          className="content-paper"
          sx={{
            p: 4,
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
            }
          }}
        >
          {/* Language Dropdown */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <LanguageDropdown
              currentLanguage={currentLanguage}
              changeLanguage={handleLanguageChange}
            />
          </Box>

          {/* iPhone Mockups Section - Only visible in Step 1 */}
          {activeStep === 0 && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: { xs: 2, sm: 3, md: 4 },
              mb: 5,
              mt: 3,
              overflow: 'visible',
              px: 2
            }}>
              {[
                'https://r2-internal.hkstylist.com/mockup1.png',
                'https://r2-internal.hkstylist.com/mockup2.png',
                'https://r2-internal.hkstylist.com/mockup3.png'
              ].map((imageUrl, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: '0 0 auto',
                    maxWidth: { xs: '120px', sm: '150px', md: '180px' },
                    maxHeight: { xs: '240px', sm: '300px', md: '360px' },
                    transform: index === 1 ? 'scale(1.05)' : 'scale(1)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: index === 1 ? 'scale(1.1)' : 'scale(1.05)'
                    }
                  }}
                >
                  <img
                    src={imageUrl}
                    alt={`HKStylist App Mockup ${index + 1}`}
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      display: 'block',
                      borderRadius: '12px',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
                    }}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      console.error(`Failed to load mockup image ${index + 1}`);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}

          {/* Header */}
          <Box className="header" sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              className="title"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              {t('quickOnboarding.title', 'Quick Business Setup')}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              className="subtitle"
              sx={{ mb: 3, maxWidth: '600px', mx: 'auto' }}
            >
              {t('quickOnboarding.subtitle', 'Set up your salon business account in just 3 simple steps')}
            </Typography>
          </Box>

          {/* Progress Stepper */}
          <Box className="stepper" sx={{ mb: 4 }}>
            <Stepper
              activeStep={activeStep}
              orientation={isMobile ? 'vertical' : 'horizontal'}
            >
              {steps.map((step) => (
                <Step key={step.key}>
                  <StepLabel>
                    <Typography variant="subtitle2" className="step-label" sx={{ fontWeight: 600 }}>
                      {step.label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" className="error-alert" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Step Content */}
          <Box>{renderStep()}</Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default QuickOnboarding;
